import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select';
import { 
  Banknote, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Info, 
  RefreshCw, 
  Building2, 
  IndianRupee, 
  X, 
  ArrowUpRight,
  ShieldAlert,
  Search
} from 'lucide-react';
import { getSupabaseClient } from '@/src/lib/supabase';
import { toast } from 'sonner';

export interface PendingSettlement {
  tenant_id: string;
  business_name: string;
  due_amount: number;
  oldest_pending_since: string;
  [key: string]: any;
}

export function MerchantSettlementsView({ onOpenPayouts }: { onOpenPayouts?: () => void } = {}) {
  const [settlements, setSettlements] = useState<PendingSettlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Settlement dialog state
  const [selectedSettlement, setSelectedSettlement] = useState<PendingSettlement | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'NEFT' | 'UPI' | 'IMPS' | 'Cheque'>('NEFT');
  const [referenceNote, setReferenceNote] = useState('');
  const [processing, setProcessing] = useState(false);

  // Task 6: Merchant online collections state (platform_collections grouped by tenant)
  const [onlineCollections, setOnlineCollections] = useState<Array<{
    tenant_id: string;
    business_name: string;
    pending_total: number;
    last_collection: string;
  }>>([]);
  const [loadingOnlineCollections, setLoadingOnlineCollections] = useState(false);
  const [settleCollectionModal, setSettleCollectionModal] = useState<{
    tenant_id: string;
    business_name: string;
    pending_total: number;
  } | null>(null);
  const [settleUtr, setSettleUtr] = useState('');
  const [settlingCollection, setSettlingCollection] = useState(false);

  const fetchSettlements = async () => {
    setLoading(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_pending_settlements');
      if (error) {
        console.warn('get_pending_settlements error:', error);
        toast.error('Failed to load pending settlements: ' + error.message);
        setSettlements([]);
      } else {
        const mapped: PendingSettlement[] = (data || []).map((item: any) => ({
          tenant_id: item.tenant_id || item.id,
          business_name: item.business_name || item.merchant_name || item.name || 'Merchant',
          due_amount: Number(item.due_amount ?? item.amount ?? item.total_due ?? 0),
          oldest_pending_since: item.oldest_pending_since || item.pending_since || item.created_at || new Date().toISOString(),
          ...item
        }));
        setSettlements(mapped);
      }
    } catch (err: any) {
      console.error('Error fetching settlements:', err);
      toast.error('Error loading pending settlements');
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOnlineCollections = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoadingOnlineCollections(true);
    try {
      const { data, error } = await supabase
        .from('platform_collections')
        .select(`
          id,
          tenant_id,
          merchant_share,
          settlement_status,
          created_at,
          tenants ( id, business_name, brand_name )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.warn('Error fetching platform_collections for super admin:', error);
      } else {
        const groups: Record<string, {
          tenant_id: string;
          business_name: string;
          pending_total: number;
          last_collection: string;
        }> = {};

        (data || []).forEach((row: any) => {
          const tId = row.tenant_id;
          const tName = row.tenants?.brand_name || row.tenants?.business_name || 'Business';
          if (!groups[tId]) {
            groups[tId] = {
              tenant_id: tId,
              business_name: tName,
              pending_total: 0,
              last_collection: row.created_at
            };
          }
          if (row.settlement_status === 'pending') {
            groups[tId].pending_total += Number(row.merchant_share || 0);
          }
          if (new Date(row.created_at) > new Date(groups[tId].last_collection)) {
            groups[tId].last_collection = row.created_at;
          }
        });

        setOnlineCollections(Object.values(groups));
      }
    } catch (err: any) {
      console.error('fetchOnlineCollections error:', err);
    } finally {
      setLoadingOnlineCollections(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
    fetchOnlineCollections();
  }, []);

  const handleSettleOnlineCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settleCollectionModal || !settleUtr.trim()) {
      toast.error('Please enter transfer reference (UTR)');
      return;
    }
    const supabase = getSupabaseClient();
    if (!supabase) return;

    setSettlingCollection(true);
    try {
      const { data, error } = await supabase.rpc('settle_platform_collections', {
        p_tenant_id: settleCollectionModal.tenant_id,
        p_settlement_ref: settleUtr.trim()
      });

      if (error) {
        toast.error(error.message || 'Settlement failed');
        return;
      }

      const settledAmt = data?.settled_amount ?? settleCollectionModal.pending_total;
      toast.success(`Settled ₹${Number(settledAmt || 0).toFixed(2)} for "${settleCollectionModal.business_name}"! UTR: ${settleUtr.trim()}`);
      setSettleCollectionModal(null);
      setSettleUtr('');
      await fetchOnlineCollections();
    } catch (err: any) {
      console.error('settle_platform_collections error:', err);
      toast.error(err.message || 'Settlement failed');
    } finally {
      setSettlingCollection(false);
    }
  };

  const openSettlementDialog = (settlement: PendingSettlement) => {
    setSelectedSettlement(settlement);
    setPaymentMethod('NEFT');
    setReferenceNote('');
  };

  const closeSettlementDialog = () => {
    if (processing) return;
    setSelectedSettlement(null);
    setReferenceNote('');
  };

  const handleConfirmSettlement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSettlement) return;

    if (!referenceNote.trim()) {
      toast.error('Kripya reference note / UTR number dalein');
      return;
    }

    setProcessing(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      toast.error('Database connection unavailable');
      setProcessing(false);
      return;
    }

    const tenantId = selectedSettlement.tenant_id;
    const amount = selectedSettlement.due_amount;
    const method = paymentMethod;
    const note = referenceNote.trim();

    try {
      // Primary RPC call with p_ prefixes
      let { data, error } = await supabase.rpc('record_manual_settlement', {
        p_tenant_id: tenantId,
        p_amount: amount,
        p_method: method,
        p_reference_note: note
      });

      // Fallback if parameter names don't use p_ prefix in the DB definition
      if (error && (error.message?.includes('could not find') || error.code === 'PGRST202')) {
        const fallbackRes = await supabase.rpc('record_manual_settlement', {
          tenant_id: tenantId,
          amount: amount,
          method: method,
          reference_note: note
        });
        error = fallbackRes.error;
        data = fallbackRes.data;
      }

      // Second fallback in case payment_method was used instead of method
      if (error && (error.message?.includes('could not find') || error.code === 'PGRST202')) {
        const fallbackRes2 = await supabase.rpc('record_manual_settlement', {
          p_tenant_id: tenantId,
          p_amount: amount,
          p_payment_method: method,
          p_reference_note: note
        });
        error = fallbackRes2.error;
        data = fallbackRes2.data;
      }

      if (error) {
        throw error;
      }

      toast.success(`Settlement of ₹${amount.toLocaleString('en-IN')} for "${selectedSettlement.business_name}" marked as settled!`);
      // Remove row from list on success
      setSettlements(prev => prev.filter(s => s.tenant_id !== tenantId));
      setSelectedSettlement(null);
      setReferenceNote('');
    } catch (err: any) {
      console.error('record_manual_settlement error:', err);
      toast.error(err.message || 'Settlement record karne mein error aaya');
    } finally {
      setProcessing(false);
    }
  };

  const formatPendingDate = (dateStr: string) => {
    if (!dateStr) return 'Unknown';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredSettlements = settlements.filter(s => 
    s.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.tenant_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDue = settlements.reduce((sum, s) => sum + (Number(s.due_amount) || 0), 0);

  return (
    <div className="space-y-6">
      {/* Top Header & Metrics */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Banknote className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            Merchant Settlements
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            BahiBox shared account se aaye hue payments ko merchants ke bank accounts mein manually settle karein.
          </p>
        </div>

        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          {onOpenPayouts && (
            <Button
              type="button"
              onClick={onOpenPayouts}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 gap-1.5 shadow-sm"
            >
              <ArrowUpRight size={15} />
              <span>Open Payouts (Main Hub)</span>
            </Button>
          )}

          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchSettlements} 
            disabled={loading}
            className="h-9 gap-2 text-xs"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Total Pending Amount</p>
                <h3 className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">
                  ₹{totalDue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h3>
              </div>
              <div className="h-11 w-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <IndianRupee size={22} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Merchants Awaiting Payout</p>
                <h3 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">
                  {settlements.length}
                </h3>
              </div>
              <div className="h-11 w-11 rounded-xl bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <Building2 size={22} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Gateway Status</p>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-1 flex items-center gap-1.5">
                  <CheckCircle2 size={18} className="text-emerald-500" />
                  BahiBox Shared Escrow
                </h3>
              </div>
              <div className="h-11 w-11 rounded-xl bg-purple-50 dark:bg-purple-950/50 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Clock size={22} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main List Card */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardHeader className="p-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-lg font-bold">Pending Merchant Settlements</CardTitle>
              <CardDescription className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                List of merchants with unpaid balances accumulated via the platform's central Razorpay gateway.
              </CardDescription>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search merchant..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-primary" />
              <p className="font-medium text-sm">Loading pending settlements...</p>
            </div>
          ) : filteredSettlements.length === 0 ? (
            <div className="p-12 text-center">
              <div className="h-16 w-16 mx-auto rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-3">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">No Pending Settlements</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto mt-1">
                {searchQuery 
                  ? 'No merchants matched your search query.' 
                  : 'All merchant funds collected through BahiBox shared gateway have been settled.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3.5">Business Name</th>
                    <th className="px-5 py-3.5">Due Amount</th>
                    <th className="px-5 py-3.5">Pending Since</th>
                    <th className="px-5 py-3.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {filteredSettlements.map((item) => (
                    <tr 
                      key={item.tenant_id} 
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                          <Building2 size={16} className="text-slate-400 shrink-0" />
                          <span>{item.business_name}</span>
                        </div>
                        <div className="text-xs text-slate-400 font-mono mt-0.5 ml-6 truncate max-w-xs">
                          Tenant ID: {item.tenant_id}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-bold text-base text-emerald-600 dark:text-emerald-400">
                          ₹{item.due_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 font-medium">
                          <Clock size={15} className="text-amber-500 shrink-0" />
                          <span>Pending since {formatPendingDate(item.oldest_pending_since)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="outline"
                            size="sm" 
                            onClick={() => openSettlementDialog(item)}
                            className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold h-9 px-4 gap-1.5"
                          >
                            <CheckCircle2 size={16} />
                            Mark as Settled
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task 6: Merchant online collections table (platform_collections grouped by tenant) */}
      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <CardHeader className="p-5 border-b border-slate-200 dark:border-slate-800 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <Banknote size={18} className="text-primary" />
              <span>Merchant online collections</span>
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 mt-0.5">
              Online booking advances & collections held in platform account, grouped by business.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchOnlineCollections}
            disabled={loadingOnlineCollections}
            className="h-8 text-xs border-slate-200 dark:border-slate-700"
          >
            <RefreshCw size={12} className={loadingOnlineCollections ? 'animate-spin mr-1' : 'mr-1'} />
            <span>Refresh</span>
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loadingOnlineCollections ? (
            <div className="p-10 text-center text-slate-500 text-xs">
              <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin text-primary" />
              Loading online collections...
            </div>
          ) : onlineCollections.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-xs">
              No online collections recorded in platform account.
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-xs uppercase font-semibold text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3.5">Business Name</th>
                  <th className="px-5 py-3.5">Pending Total</th>
                  <th className="px-5 py-3.5">Last Collection</th>
                  <th className="px-5 py-3.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {onlineCollections.map((grp) => (
                  <tr key={grp.tenant_id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        <Building2 size={16} className="text-slate-400 shrink-0" />
                        <span>{grp.business_name}</span>
                      </div>
                      <div className="text-xs text-slate-400 font-mono mt-0.5 ml-6 truncate max-w-xs">
                        {grp.tenant_id}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`font-bold text-base ${grp.pending_total > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500'}`}>
                        ₹{grp.pending_total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-600 dark:text-slate-300">
                      {grp.last_collection ? new Date(grp.last_collection).toLocaleString() : '—'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={grp.pending_total <= 0}
                        onClick={() => {
                          setSettleCollectionModal(grp);
                          setSettleUtr('');
                        }}
                        className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold h-8 px-3 text-xs gap-1.5 disabled:opacity-40"
                      >
                        <CheckCircle2 size={14} />
                        Mark settled
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Mandatory Disclaimer Note from Prompt */}
      <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/30 p-4 text-amber-900 dark:text-amber-200 text-sm flex items-start gap-3 shadow-sm">
        <Info size={20} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="space-y-1">
          <p className="font-semibold text-amber-950 dark:text-amber-100">
            Important Note on Settlements:
          </p>
          <p className="leading-relaxed text-amber-800 dark:text-amber-300">
            Yeh sirf BahiBox ke shared account se aaye paise ke liye hai. Merchants jo apna Razorpay account use kar rahe hain, unka paisa seedha unke bank mein jaata hai — unhe yahan settle karne ki zaroorat nahi.
          </p>
          <p className="text-xs text-amber-700/80 dark:text-amber-400/80 pt-1 italic">
            <strong>Future Roadmap:</strong> Jab Razorpay Payout API access mil jaaye, "Mark as Settled" ke bagal mein ek "Send via Razorpay Payout" option add hoga jo automatically bank transfer kar dega.
          </p>
        </div>
      </div>

      {/* Mark as Settled Confirmation Modal */}
      {selectedSettlement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/40">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Banknote className="h-5 w-5 text-emerald-600" />
                  Record Manual Settlement
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Confirm payout for {selectedSettlement.business_name}
                </p>
              </div>
              <button
                onClick={closeSettlementDialog}
                disabled={processing}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleConfirmSettlement} className="p-6 space-y-4">
              {/* Read-only Due Amount */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Settlement Amount (Read-only)
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-slate-400">
                    ₹
                  </span>
                  <Input
                    type="text"
                    readOnly
                    disabled
                    value={selectedSettlement.due_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    className="pl-8 h-11 bg-slate-100 dark:bg-slate-800 font-bold text-base text-slate-900 dark:text-slate-100 cursor-not-allowed border-slate-300 dark:border-slate-700"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Amount locked: Backend exact match calculation maangta hai.
                </p>
              </div>

              {/* Payment Method Dropdown */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Payment Method
                </label>
                <Select
                  value={paymentMethod}
                  onValueChange={(val: any) => setPaymentMethod(val)}
                  disabled={processing}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select Payment Method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEFT">NEFT (National Electronic Funds Transfer)</SelectItem>
                    <SelectItem value="UPI">UPI (Unified Payments Interface)</SelectItem>
                    <SelectItem value="IMPS">IMPS (Immediate Payment Service)</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reference Note Text Field */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Reference Note (UTR / Cheque No.) *
                </label>
                <Input
                  required
                  placeholder="e.g. UTR-20240918-098234 or Cheque #402911"
                  value={referenceNote}
                  onChange={(e) => setReferenceNote(e.target.value)}
                  disabled={processing}
                  className="h-11"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Bank transaction UTR number ya cheque number zaroor enter karein.
                </p>
              </div>

              {/* Actions */}
              <div className="pt-3 flex items-center justify-end gap-3 border-t border-slate-200 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeSettlementDialog}
                  disabled={processing}
                  className="h-10 px-4"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={processing || !referenceNote.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-10 px-5 gap-2 shadow-sm"
                >
                  {processing ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Confirm Settlement
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settle Online Collection Dialog */}
      {settleCollectionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800 mb-4">
              <div className="flex items-center gap-2 text-emerald-600">
                <Banknote size={20} />
                <h3 className="font-bold text-base text-slate-900 dark:text-white">
                  Settle Online Collections
                </h3>
              </div>
              <button
                onClick={() => setSettleCollectionModal(null)}
                disabled={settlingCollection}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSettleOnlineCollection} className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Business:</span>
                  <span className="font-bold text-slate-900 dark:text-white">{settleCollectionModal.business_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Pending Online Share:</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                    ₹{settleCollectionModal.pending_total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1.5">
                  Transfer Reference / UTR Number <span className="text-rose-500">*</span>
                </label>
                <Input
                  required
                  autoFocus
                  placeholder="e.g. UTR123456789012"
                  value={settleUtr}
                  onChange={(e) => setSettleUtr(e.target.value)}
                  disabled={settlingCollection}
                  className="h-10 text-xs"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Bank transfer UTR number entered here will be saved in platform collections audit ledger.
                </p>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-200 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSettleCollectionModal(null)}
                  disabled={settlingCollection}
                  className="h-9 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={settlingCollection || !settleUtr.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-9 text-xs px-4 gap-1.5 shadow-sm"
                >
                  {settlingCollection ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      Settling...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={13} />
                      Confirm Settlement
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
